export interface Palette {
  name: string;
  textColor: string;
  outlineColor: string | null;
  colors: string[];
}

export const PaletteDefault: Palette = {
  name: 'default',
  textColor: '#000000',
  outlineColor: '#000000',
  colors: ['#FFFFFF'],
};

//
// WHITE TEXT PALETTES (dark fills)
//

export const PaletteDeep: Palette = {
  name: 'deep',
  textColor: '#FFFFFF',
  outlineColor: null,
  colors: ['#1F3A8A', '#7C2D12', '#065F46', '#5B21B6', '#9F1239', '#92400E', '#0F766E', '#374151'],
};

export const PaletteProfessional: Palette = {
  name: 'professional',
  textColor: '#FFFFFF',
  outlineColor: null,
  colors: ['#2C5282', '#9B2C2C', '#276749', '#6B46C1', '#B7791F', '#2B6CB0', '#97266D', '#4A5568'],
};

export const PaletteAcademic: Palette = {
  name: 'academic',
  textColor: '#FFFFFF',
  outlineColor: null,
  colors: ['#1B5E20', '#0D47A1', '#4A148C', '#880E4F', '#BF360C', '#827717', '#004D40', '#263238'],
};

export const PaletteGrayscaleDark: Palette = {
  name: 'grayscaleDark',
  textColor: '#FFFFFF',
  outlineColor: null,
  colors: ['#222222', '#333333', '#444444', '#555555', '#666666', '#777777', '#888888', '#999999'],
};

//
// BLACK TEXT PALETTES (light fills)
// All colors >=4.5:1 contrast with black text
//

export const PaletteSoft: Palette = {
  name: 'soft',
  textColor: '#000000',
  outlineColor: null,
  colors: ['#93C5FD', '#FCA5A5', '#86EFAC', '#FCD34D', '#C4B5FD', '#FDBA74', '#A5F3FC', '#D1D5DB'],
};

export const PalettePastelStrong: Palette = {
  name: 'pastelStrong',
  textColor: '#000000',
  outlineColor: null,
  colors: ['#A7D3F2', '#F2B8C6', '#F2E394', '#B8E0C2', '#D5B6E8', '#F7C59F', '#BEE3F8', '#E2E8F0'],
};

export const PaletteWarmLight: Palette = {
  name: 'warmLight',
  textColor: '#000000',
  outlineColor: null,
  colors: ['#FDBA74', '#FCD34D', '#FCA5A5', '#F9A8D4', '#DDD6FE', '#BFDBFE', '#BBF7D0', '#E5E7EB'],
};

export const PaletteCoolLight: Palette = {
  name: 'coolLight',
  textColor: '#000000',
  outlineColor: null,
  colors: ['#BAE6FD', '#A5F3FC', '#C7D2FE', '#DDD6FE', '#BFE3C0', '#E0F2FE', '#D1FAE5', '#E2E8F0'],
};

//
// NEW PALETTES - RGB / ColorBrewer / Tableau / D3 / Material / Tailwind
//

export const PaletteRgb: Palette = {
  name: 'rgb',
  textColor: '#000000',
  outlineColor: null,
  colors: ['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FF8000', '#800080'],
};

export const PaletteColorBrewer: Palette = {
  name: 'colorbrewer',
  textColor: '#000000',
  outlineColor: null,
  colors: ['#1B9E77', '#D95F02', '#7570B3', '#E7298A', '#66A61E', '#E6AB02', '#A6761D', '#666666'],
};

export const PaletteTableau: Palette = {
  name: 'tableau',
  textColor: '#000000',
  outlineColor: null,
  colors: [
    '#4E79A7',
    '#F28E2B',
    '#E15759',
    '#76B7B2',
    '#59A14F',
    '#EDC948',
    '#B07AA1',
    '#FF9DA7',
    '#9C755F',
    '#BAB0AC',
  ],
};

export const PaletteD3Default: Palette = {
  name: 'd3default',
  textColor: '#000000',
  outlineColor: null,
  colors: [
    '#1F77B4',
    '#FF7F0E',
    '#2CA02C',
    '#D62728',
    '#9467BD',
    '#8C564B',
    '#E377C2',
    '#7F7F7F',
    '#BCBD22',
    '#17BECF',
  ],
};

export const PaletteMaterial: Palette = {
  name: 'material',
  textColor: '#000000',
  outlineColor: null,
  colors: ['#4285F4', '#EA4335', '#FBBC05', '#34A853', '#FF6D01', '#46BDC6', '#7BAAF7', '#F6AEA9'],
};

export const PaletteTailwind: Palette = {
  name: 'tailwind',
  textColor: '#000000',
  outlineColor: null,
  colors: ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'],
};

export const PaletteModern: Palette = {
  name: 'modern',
  textColor: '#000000',
  outlineColor: null,
  colors: ['#4E79A7', '#F28E2B', '#59A14F', '#E15759', '#B07AA1', '#76B7B2', '#EDC948', '#FF9DA7'],
};

export const palettes: Record<string, Palette> = {
  default: PaletteDefault,

  // white text (dark fills)
  deep: PaletteDeep,
  professional: PaletteProfessional,
  academic: PaletteAcademic,
  grayscaleDark: PaletteGrayscaleDark,

  // black text (light fills)
  soft: PaletteSoft,
  pastelStrong: PalettePastelStrong,
  warmLight: PaletteWarmLight,
  coolLight: PaletteCoolLight,

  // new palettes (light text / dark fills for contrast)
  rgb: PaletteRgb,
  colorbrewer: PaletteColorBrewer,
  tableau: PaletteTableau,
  d3default: PaletteD3Default,
  material: PaletteMaterial,
  tailwind: PaletteTailwind,
  modern: PaletteModern,
};

export function getPalette(name: string): Palette {
  return palettes[name] || PaletteDefault;
}

export function getPaletteColors(name: string): string[] {
  const palette = getPalette(name);
  return palette.colors;
}

export function getTextColor(name: string): string {
  const palette = getPalette(name);
  return palette.textColor;
}

export function getOutlineColor(name: string): string | null {
  const palette = getPalette(name);
  return palette.outlineColor;
}

export function getAllPaletteNames(): string[] {
  return Object.keys(palettes);
}
